import { useMutation } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import { useAlert } from "@/hooks/useAlert";
import { useQueryParam } from "@/hooks/useQueryParam";
import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  AGENT_LOGIN_URL,
  BASIC_PORTAL_AUTHORIZATION,
  TOKEN_URL,
} from "@/utils/apiUrls";
import {
  accessTokenAtom,
  AlertTypes,
  isAgentAtom,
  refreshTokenAtom,
} from "@/utils/atoms";
import { agentLoginStatus } from "@/utils/authentication";
import { createReferenceId } from "@/utils/initializeSentry";

export const AgentLogin: React.FC = () => {
  const setAccessToken = useSetAtom(accessTokenAtom);
  const setRefreshToken = useSetAtom(refreshTokenAtom);
  const setIsAgent = useSetAtom(isAgentAtom);
  const { setDataContext } = useContext(DataContext);
  const showAlert = useAlert();
  const [customer] = useQueryParam("customer");
  const [hash] = useQueryParam("hash");
  const [user] = useQueryParam("user");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const referenceId = createReferenceId();

  const { mutate: loginAsAgent, isError: agentDataIsError } = useMutation(
    ["agentLogin"],
    async (data: { customer: string; hash: string; user: string }) => {
      const tokenRes = await fetch(
        `${TOKEN_URL.toString()}?grant_type=client_credentials&scope=all`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: BASIC_PORTAL_AUTHORIZATION,
            "X-Reference-Id": referenceId,
          },
        },
      );

      if (!tokenRes.ok) {
        throw new Error(`Token request failed: ${tokenRes.status}`);
      }

      const tokenJson = await tokenRes.json();
      if (!tokenJson.access_token) {
        throw new Error("No access token");
      }

      const agentRes = await fetch(AGENT_LOGIN_URL.toString(), {
        method: "POST",
        body: JSON.stringify({
          customer: data.customer,
          hash: data.hash,
          user: data.user,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenJson.access_token}`,
        },
      });

      if (!agentRes.ok) {
        throw new Error(`Agent login failed: ${agentRes.status}`);
      }

      const agentLoginData = await agentRes.json();
      if (!agentLoginData?.access_token) {
        throw new Error("Agent login response missing access_token");
      }

      return agentLoginData;
    },
    {
      onError: () =>
        showAlert({
          text: t("Error.agentLogin.failed"),
          type: AlertTypes.error,
        }),
      onSuccess: (agentLoginData: any) => {
        if (agentLoginData?.access_token) {
          localStorage.clear();

          setDataContext({
            allowedMaxOnboardingStep: 1,
            isEmailVerified: true,
          });

          setAccessToken(agentLoginData.access_token);
          setRefreshToken(agentLoginData.refresh_token || agentLoginStatus);
          setIsAgent(true);

          navigate(portalRoutes.cockpit.base, { replace: true });
        }
      },
    },
  );

  useEffect(() => {
    if (customer && hash && user) {
      loginAsAgent({ customer, hash, user });
    }
  }, [customer, hash, loginAsAgent, user]);

  if (!customer || !hash || !user || agentDataIsError) {
    return <Navigate to="/404" replace />;
  }

  return <BrandLoadingScreen />;
};
